/** @jsx jsx */
import { jsx } from '@emotion/react'
import { useEffect, useState } from 'react'
import {
  BoardItemFragment,
  useSearchPlaceholderTemplatesQuery
} from '../../../../generated/graphql'

import PlaceholderAutoComplete from './PlaceholderAutoComplete'
import useEnvironment from '../../../../lib/hooks/useEnvironment'

type PlaceholderAutoCompleteWrapperProps = {
  /** Campus used to filter placeholders */
  campus?: string
  /** Current Group Id */
  groupId: string
  /** Event callback when a new Placeholder is selected */
  onChange: (placeholder: BoardItemFragment) => void
}

const defaultProps: Pick<PlaceholderAutoCompleteWrapperProps, 'campus'> = {
  campus: undefined
}

export const PlaceholderAutoCompleteWrapper = ({
  campus,
  groupId,
  onChange
}: PlaceholderAutoCompleteWrapperProps) => {
  const [inputValue, setInputValue] = useState('')
  const [placeholders, setPlaceholders] = useState<BoardItemFragment[]>([])
  const environment = useEnvironment()

  const { data } = useSearchPlaceholderTemplatesQuery({
    variables: {
      campusCode: campus,
      environment,
      searchText: inputValue
    },
    // Don't use cached results for the first search in case there are any changes to the placeholder status
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first', // Used for any subsequent executions
    // skip query if text is empty
    skip: !inputValue,
    context: { useApolloNetworkStatus: false }
  })

  const handleInputChange = (inputVal: string) => {
    setInputValue(inputVal)
  }

  useEffect(() => {
    /**
     * This effect avoids re-rendering the menu when query is loading.
     * data is undefined when the query is loading.
     * Thus if we use query.data directly, the menu renders as empty when query is loading.
     */
    if (data) {
      setPlaceholders(data.searchPlaceholderTemplates)
    }
  }, [data])

  return (
    <PlaceholderAutoComplete
      id={`${groupId}-placeholder-autocomplete`}
      groupId={groupId}
      inputValue={inputValue}
      onChange={onChange}
      onInputChange={handleInputChange}
      placeholderTemplates={placeholders}
    />
  )
}

PlaceholderAutoCompleteWrapper.defaultProps = defaultProps

export default PlaceholderAutoCompleteWrapper
